import axios, { AxiosResponse } from 'axios';
import { auth } from '@/lib/firebase';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

const adminApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

adminApi.interceptors.request.use(async (config) => {
  if (auth.currentUser) {
    const token = await auth.currentUser.getIdToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

export type MetricsTrendDirection = 'up' | 'down' | 'flat';

export type DailyMetricsRow = {
  date: string;
  run_count: number;
  total_cost: number;
  total_duration_ms: number;
  completed_count: number;
  avg_cost?: number;
  avg_duration_ms?: number;
};

export type AnalysisRunSummary = {
  run_id?: string;
  session_id?: string;
  user_id?: string;
  status?: string;
  started_at?: string;
  ended_at?: string;
  total_cost_est?: number;
  total_wall_clock_ms?: number;
  total_actual_input_tokens?: number | null;
  total_actual_output_tokens?: number | null;
  script_chars?: number;
  script_page_count?: number;
  error_type?: string | null;
};

export type AnalysisMetricsTrends = {
  runs: AnalysisRunSummary[];
  daily: DailyMetricsRow[];
  summary: {
    run_count: number;
    avg_cost_est: number;
    avg_duration_ms: number;
    completed_count: number;
  };
  trends: {
    cost_direction: MetricsTrendDirection;
    duration_direction: MetricsTrendDirection;
    recent_avg_cost_est: number;
    prior_avg_cost_est: number;
    recent_avg_duration_ms: number;
    prior_avg_duration_ms: number;
  };
  agents: AgentMetricsTrend[];
};

export type AgentMetricsTrend = {
  phase_name: string;
  label: string;
  run_count: number;
  avg_cost_est: number;
  avg_duration_ms: number;
  cost_direction: MetricsTrendDirection;
  duration_direction: MetricsTrendDirection;
  recent_avg_cost_est: number;
  prior_avg_cost_est: number;
  recent_avg_duration_ms: number;
  prior_avg_duration_ms: number;
};

export type ScriptInstrumentationMetrics = {
  engagement_score: number | null;
  total_scenes: number | null;
  total_events: number | null;
  total_candidate_beats: number | null;
  total_themes: number | null;
  qualified_beat_count: number | null;
  confirmed_beat_count: number | null;
};

export type EmissionCheckRow = {
  key: string;
  label: string;
  present: boolean;
  value?: string | number | null;
};

export type ScorePayloadEmission = {
  has_issues: boolean;
  scores: EmissionCheckRow[];
  payload: EmissionCheckRow[];
  agent_fields: EmissionCheckRow[];
  missing_scores?: string[];
  missing_payload?: string[];
  missing_agent_fields?: string[];
  summary?: {
    scores_present: number;
    scores_missing: number;
    payload_present: number;
    payload_missing: number;
    agent_fields_present: number;
    agent_fields_missing: number;
  };
};

export type ScriptInstrumentationRow = {
  session_id?: string;
  analysis_session_id?: string;
  script_title?: string;
  status: string;
  instrumentation: ScriptInstrumentationMetrics | null;
  emission?: ScorePayloadEmission | null;
  critical_errors?: string[];
};

export type ScriptInstrumentationResponse = {
  project_id: string;
  project_name?: string;
  scripts: ScriptInstrumentationRow[];
};

type TrendsResponse = {
  status: string;
  trends: AnalysisMetricsTrends;
};

export async function getAnalysisMetricsTrends(params?: {
  days?: number;
  limit?: number;
}): Promise<AnalysisMetricsTrends> {
  const response: AxiosResponse<TrendsResponse> = await adminApi.get(
    '/api/admin/analysis-metrics/trends',
    { params },
  );
  if (response.data.status !== 'success') {
    throw new Error('Failed to load analysis metrics');
  }
  return response.data.trends;
}

export async function setUserAdminStatus(email: string, is_admin: boolean): Promise<void> {
  const response = await adminApi.put('/api/admin/users/admin-status', { email, is_admin });
  if (response.data.status !== 'success') {
    throw new Error('Failed to update admin status');
  }
}

export async function setUserReviewerStatus(email: string, is_reviewer: boolean): Promise<void> {
  const response = await adminApi.put('/api/admin/users/reviewer-status', { email, is_reviewer });
  if (response.data.status !== 'success') {
    throw new Error('Failed to update reviewer status');
  }
}

export async function listUserRoles(): Promise<{ admins: string[]; reviewers: string[] }> {
  const response = await adminApi.get('/api/admin/users/roles');
  if (response.data.status !== 'success') {
    throw new Error('Failed to load user roles');
  }
  return {
    admins: Array.isArray(response.data.admins) ? response.data.admins : [],
    reviewers: Array.isArray(response.data.reviewers) ? response.data.reviewers : [],
  };
}

export type ThresholdSpec = {
  label: string;
  min: number;
  max: number;
};

export type AdminThresholdsResponse = {
  thresholds: Record<string, number>;
  spec: Record<string, ThresholdSpec>;
};

export async function getAdminThresholds(): Promise<AdminThresholdsResponse> {
  const response = await adminApi.get('/api/admin/thresholds');
  if (response.data.status !== 'success') {
    throw new Error('Failed to load thresholds');
  }
  return { thresholds: response.data.thresholds ?? {}, spec: response.data.spec ?? {} };
}

export async function updateAdminThresholds(
  updates: Record<string, number>,
): Promise<Record<string, number>> {
  const response = await adminApi.put('/api/admin/thresholds', { updates });
  if (response.data.status !== 'success') {
    throw new Error('Failed to update thresholds');
  }
  return response.data.thresholds ?? {};
}

export type ScoringConfigResponse = {
  firestore_doc: string;
  sections: Record<string, unknown>;
  defaults: Record<string, unknown>;
  admin_thresholds: Record<string, number>;
  admin_threshold_spec: Record<string, ThresholdSpec>;
  pillar_weights?: Record<string, unknown>;
};

export async function getScoringConfig(): Promise<ScoringConfigResponse> {
  const response = await adminApi.get('/api/admin/scoring-config');
  if (response.data.status !== 'success') {
    throw new Error('Failed to load scoring config');
  }
  return response.data as ScoringConfigResponse;
}

export async function updateScoringConfig(
  sections: Record<string, unknown>,
): Promise<ScoringConfigResponse> {
  const response = await adminApi.put('/api/admin/scoring-config', { sections });
  if (response.data.status !== 'success') {
    throw new Error('Failed to update scoring config');
  }
  return response.data as ScoringConfigResponse;
}

export async function getScriptInstrumentation(
  projectId: string,
): Promise<ScriptInstrumentationResponse> {
  const response = await adminApi.get('/api/admin/script-instrumentation', {
    params: { project_id: projectId },
  });
  if (response.data.status !== 'success') {
    throw new Error('Failed to load script instrumentation');
  }
  return {
    project_id: response.data.project_id,
    project_name: response.data.project_name,
    scripts: response.data.scripts ?? [],
  };
}

export type StepAnalysisStep = {
  step_id: string;
  agent_id?: string;
  strand_id?: string | null;
  executor_kind?: string;
  executor_id?: string;
  status?: string;
  level: 'ok' | 'fallthrough' | 'failed';
  label: string;
  detail: string;
  created_at?: string;
  fell_through?: boolean;
};

export type StepAnalysisRun = {
  script_id: string;
  session_id?: string;
  script_name: string;
  project_id?: string;
  date_run: string;
  step_count: number;
  ok_count: number;
  fallthrough_count: number;
  failed_count: number;
  steps: StepAnalysisStep[];
};

export type StepAnalysisResponse = {
  project_id?: string | null;
  runs: StepAnalysisRun[];
};

export async function getStepAnalysis(params?: {
  projectId?: string;
  limit?: number;
}): Promise<StepAnalysisResponse> {
  const response = await adminApi.get('/api/admin/step-analysis', {
    params: {
      project_id: params?.projectId,
      limit: params?.limit ?? 40,
    },
  });
  if (response.data.status !== 'success') {
    throw new Error('Failed to load step analysis');
  }
  return {
    project_id: response.data.project_id,
    runs: response.data.runs ?? [],
  };
}

export type MlExecutorMetrics = {
  executor_id: string;
  slug: string;
  attempts: number;
  ml_ok: number;
  fallthrough: number;
  failed: number;
  success_rate: number | null;
  fallthrough_rate: number | null;
  latency_p95_ms: number | null;
  latency_samples: number;
};

export type MlMetricsResponse = {
  generated_at?: string;
  project_id?: string | null;
  days?: number | null;
  scripts_scanned: number;
  ml_steps_scanned: number;
  summary: {
    attempts: number;
    ml_ok: number;
    fallthrough: number;
    success_rate: number | null;
    fallthrough_rate: number | null;
    latency_p95_ms: number | null;
    latency_samples: number;
    latency_note?: string;
  };
  executors: MlExecutorMetrics[];
};

export async function getMlMetrics(params?: {
  projectId?: string;
  limit?: number;
  days?: number;
}): Promise<MlMetricsResponse> {
  const response = await adminApi.get('/api/admin/ml-metrics', {
    params: {
      project_id: params?.projectId,
      limit: params?.limit ?? 40,
      days: params?.days ?? 30,
    },
  });
  if (response.data.status !== 'success') {
    throw new Error('Failed to load ML metrics');
  }
  return {
    generated_at: response.data.generated_at,
    project_id: response.data.project_id,
    days: response.data.days,
    scripts_scanned: response.data.scripts_scanned ?? 0,
    ml_steps_scanned: response.data.ml_steps_scanned ?? 0,
    summary: response.data.summary ?? {
      attempts: 0,
      ml_ok: 0,
      fallthrough: 0,
      success_rate: null,
      fallthrough_rate: null,
      latency_p95_ms: null,
      latency_samples: 0,
    },
    executors: response.data.executors ?? [],
  };
}
